// Currency Exchange Utility
// Supports Frankfurter API for live rates with fallback to cached/fixed rates

import { logger } from './logger';

export type CurrencyCode = 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD' | 'AED' | 'SAR' | 'TRY' | 'SEK';

export interface ExchangeRates {
  [key: string]: {
    [key: string]: number;
  };
}

export interface ExchangeRateStatus {
  source: 'api' | 'cached' | 'fallback';
  lastUpdate: string | null;
  error: string | null;
}

// Storage key for cached rates
const CACHE_KEY = 'exchangeRates_cache';
const CACHE_TIMESTAMP_KEY = 'exchangeRates_timestamp';

// Current active rates (starts with fallback, updated by API)
let currentRates: ExchangeRates | null = null;
let rateStatus: ExchangeRateStatus = {
  source: 'fallback',
  lastUpdate: null,
  error: null,
};

// Fallback rates (used when API fails and no cache exists)
// Base: USD = 1
export const FALLBACK_RATES: ExchangeRates = {
  USD: {
    USD: 1,
    EUR: 0.95,
    GBP: 0.79,
    CAD: 1.40,
    AUD: 1.55,
    AED: 3.67,
    SAR: 3.75,
    TRY: 34.5,
    SEK: 9.47,
  },
  EUR: {
    USD: 1.05,
    EUR: 1,
    GBP: 0.83,
    CAD: 1.47,
    AUD: 1.63,
    AED: 3.86,
    SAR: 3.95,
    TRY: 36.3,
    SEK: 9.97,
  },
  GBP: {
    USD: 1.27,
    EUR: 1.20,
    GBP: 1,
    CAD: 1.77,
    AUD: 1.96,
    AED: 4.65,
    SAR: 4.75,
    TRY: 43.7,
    SEK: 11.99,
  },
  CAD: {
    USD: 0.71,
    EUR: 0.68,
    GBP: 0.56,
    CAD: 1,
    AUD: 1.11,
    AED: 2.62,
    SAR: 2.68,
    TRY: 24.6,
    SEK: 6.76,
  },
  AUD: {
    USD: 0.65,
    EUR: 0.61,
    GBP: 0.51,
    CAD: 0.90,
    AUD: 1,
    AED: 2.37,
    SAR: 2.42,
    TRY: 22.3,
    SEK: 6.11,
  },
  AED: {
    USD: 0.27,
    EUR: 0.26,
    GBP: 0.22,
    CAD: 0.38,
    AUD: 0.42,
    AED: 1,
    SAR: 1.02,
    TRY: 9.4,
    SEK: 2.58,
  },
  SAR: {
    USD: 0.27,
    EUR: 0.25,
    GBP: 0.21,
    CAD: 0.37,
    AUD: 0.41,
    AED: 0.98,
    SAR: 1,
    TRY: 9.2,
    SEK: 2.53,
  },
  TRY: {
    USD: 0.029,
    EUR: 0.028,
    GBP: 0.023,
    CAD: 0.041,
    AUD: 0.045,
    AED: 0.106,
    SAR: 0.109,
    TRY: 1,
    SEK: 0.27,
  },
  SEK: {
    USD: 0.11,
    EUR: 0.1,
    GBP: 0.083,
    CAD: 0.15,
    AUD: 0.16,
    AED: 0.39,
    SAR: 0.4,
    TRY: 3.64,
    SEK: 1,
  },
};

// Para birimi sembolleri
export const CURRENCY_SYMBOLS: Record<CurrencyCode, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  CAD: 'C$',
  AUD: 'A$',
  AED: 'AED',
  SAR: 'SAR',
  TRY: '₺',
  SEK: 'kr',
};

// Para birimi isimleri
export const CURRENCY_NAMES: Record<CurrencyCode, string> = {
  USD: 'US Dollar',
  EUR: 'Euro',
  GBP: 'British Pound',
  CAD: 'Canadian Dollar',
  AUD: 'Australian Dollar',
  AED: 'UAE Dirham',
  SAR: 'Saudi Riyal',
  TRY: 'Turkish Lira',
  SEK: 'Swedish Krona',
};

// Fixed pegs to USD (these currencies have fixed or semi-fixed rates)
const USD_PEGGED: Record<string, number> = {
  AED: 3.6725, // UAE Dirham pegged to USD
  SAR: 3.75,   // Saudi Riyal pegged to USD
};

/**
 * Load cached rates from localStorage
 */
const loadCachedRates = (): { rates: ExchangeRates | null; timestamp: string | null } => {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    const timestamp = localStorage.getItem(CACHE_TIMESTAMP_KEY);
    if (cached) {
      return { rates: JSON.parse(cached), timestamp };
    }
  } catch (e) {
    logger.warn('Failed to load cached exchange rates:', e);
  }
  return { rates: null, timestamp: null };
};

/**
 * Save rates to localStorage cache
 */
const saveCachedRates = (rates: ExchangeRates): void => {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify(rates));
    localStorage.setItem(CACHE_TIMESTAMP_KEY, new Date().toISOString());
  } catch (e) {
    logger.warn('Failed to cache exchange rates:', e);
  }
};

/**
 * Build complete exchange rate matrix from USD-based rates
 */
const buildRateMatrix = (usdRates: Record<string, number>): ExchangeRates => {
  const currencies = Object.keys(usdRates);
  const matrix: ExchangeRates = {};

  currencies.forEach(from => {
    matrix[from] = {};
    currencies.forEach(to => {
      if (from === to) {
        matrix[from][to] = 1;
      } else {
        // Convert through USD: from -> USD -> to
        const fromToUsd = 1 / usdRates[from];
        const usdToTo = usdRates[to];
        matrix[from][to] = fromToUsd * usdToTo;
      }
    });
  });

  return matrix;
};

/**
 * Fetch live exchange rates from backend API (exchange_rates table)
 * Backend cron updates rates every 12 hours from exchangerate-api.com
 */
export const fetchLiveRates = async (): Promise<{ rates: ExchangeRates; status: ExchangeRateStatus }> => {
  const { rates: cachedRates, timestamp: cachedTimestamp } = loadCachedRates();

  try {
    const token = localStorage.getItem('sso_access_token');
    const response = await fetch('/api/settings/exchange-rates', {
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      signal: AbortSignal.timeout(5000),
    });

    if (!response.ok) {
      throw new Error(`API returned ${response.status}`);
    }

    const json = await response.json();
    const rows: Array<{ currency_code: string; rate_to_usd: number }> = json.data;

    // Build USD rates object (1 USD = X currency)
    // Backend stores rate_to_usd (currency→USD), we need USD→currency = 1/rate_to_usd
    const usdRates: Record<string, number> = { USD: 1 };
    for (const row of rows) {
      if (row.currency_code === 'USD') continue;
      usdRates[row.currency_code] = 1 / row.rate_to_usd;
    }

    // Ensure pegged currencies exist even if not in DB
    if (!usdRates.AED) usdRates.AED = USD_PEGGED.AED;
    if (!usdRates.SAR) usdRates.SAR = USD_PEGGED.SAR;

    // Build complete matrix
    const rates = buildRateMatrix(usdRates);

    // Cache successful fetch
    saveCachedRates(rates);

    currentRates = rates;
    rateStatus = {
      source: 'api',
      lastUpdate: new Date().toISOString(),
      error: null,
    };

    logger.log('Exchange rates updated from backend API');
    return { rates, status: rateStatus };

  } catch (error) {
    const errorMsg = error instanceof Error ? error.message : 'Unknown error';
    logger.warn('Failed to fetch exchange rates from API:', errorMsg);

    // Try to use cached rates
    if (cachedRates) {
      currentRates = cachedRates;
      rateStatus = {
        source: 'cached',
        lastUpdate: cachedTimestamp,
        error: `API error: ${errorMsg}. Using cached rates from ${cachedTimestamp ? new Date(cachedTimestamp).toLocaleDateString('tr-TR') : 'unknown date'}.`,
      };
      logger.log('Using cached exchange rates');
      return { rates: cachedRates, status: rateStatus };
    }

    // Fall back to hardcoded rates
    currentRates = FALLBACK_RATES;
    rateStatus = {
      source: 'fallback',
      lastUpdate: null,
      error: `API error: ${errorMsg}. Using fallback rates - values may be outdated!`,
    };
    logger.log('Using fallback exchange rates');
    return { rates: FALLBACK_RATES, status: rateStatus };
  }
};

/**
 * Get current exchange rate status
 */
export const getExchangeRateStatus = (): ExchangeRateStatus => {
  return rateStatus;
};

/**
 * Get current active rates (fetched, cached, or fallback)
 */
export const getCurrentRates = (): ExchangeRates => {
  if (!currentRates) {
    // Initialize from cache or fallback
    const { rates: cachedRates, timestamp } = loadCachedRates();
    if (cachedRates) {
      currentRates = cachedRates;
      rateStatus = {
        source: 'cached',
        lastUpdate: timestamp,
        error: null,
      };
    } else {
      currentRates = FALLBACK_RATES;
      rateStatus = {
        source: 'fallback',
        lastUpdate: null,
        error: null,
      };
    }
  }
  return currentRates;
};

/**
 * Convert amount from one currency to another
 * @param amount - Amount to convert
 * @param from - Source currency code
 * @param to - Target currency code
 * @returns Converted amount
 */
export const convertCurrency = (
  amount: number,
  from: CurrencyCode,
  to: CurrencyCode
): number => {
  if (from === to) return amount;

  const rates = getCurrentRates();
  const rate = rates[from]?.[to];

  if (!rate) {
    // CRITICAL: Missing exchange rate - this would cause severe calculation errors
    // Log error and return 0 to make the issue visible (not silently wrong data)
    logger.error(`CRITICAL: Exchange rate not found for ${from} to ${to}. Amount: ${amount}`);
    // Return 0 instead of unconverted amount to prevent 30-40x inflation errors
    // This makes missing rates visible in reports rather than silently wrong
    return 0;
  }

  return amount * rate;
};

/**
 * Format currency amount with symbol
 * @param amount - Amount to format
 * @param currency - Currency code
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string
 */
export const formatCurrency = (
  amount: number,
  currency: CurrencyCode,
  decimals: number = 2
): string => {
  const symbol = CURRENCY_SYMBOLS[currency];
  const formatted = amount.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return `${symbol}${formatted}`;
};

// Marketplace -> currency mapping. Single source of truth for which marketplaces
// have a known USD conversion path. Anything not in this map is "unsupported"
// and gets silently filtered out of analyzers (SE/MX/NL/PL/BE/JP would otherwise
// be treated as USD and produce 10-30x inflated totals).
const MARKETPLACE_CURRENCY_MAP: Record<string, CurrencyCode> = {
  US: 'USD',
  UK: 'GBP',
  DE: 'EUR',
  FR: 'EUR',
  IT: 'EUR',
  ES: 'EUR',
  CA: 'CAD',
  AU: 'AUD',
  AE: 'AED',
  SA: 'SAR',
  // 2026-08-22: IE/NL/BE/SE eklendi. Bu dort pazar esik ustune ciktigi halde
  // analizlerde HIC gorunmuyordu — burada olmayan kod isSupportedMarketplace()'ten
  // false donuyor ve App.tsx satirlari SESSIZCE eliyor. Olculen 2026 YTD hacim:
  // IE 61 adet / EUR 5.753 · NL 64 / EUR 5.723 · BE 51 / EUR 3.764 · SE 27 /
  // SEK 25.572 — IE ve NL, kendi satiri olan ES'ten (42 adet / EUR 4.683) buyuk.
  IE: 'EUR',
  NL: 'EUR',
  BE: 'EUR',
  SE: 'SEK',
};

export const SUPPORTED_MARKETPLACE_CODES: ReadonlySet<string> = new Set(
  Object.keys(MARKETPLACE_CURRENCY_MAP)
);

export const isSupportedMarketplace = (
  marketplaceCode: string | null | undefined
): boolean => {
  if (!marketplaceCode) return false;
  return SUPPORTED_MARKETPLACE_CODES.has(marketplaceCode);
};

/**
 * Get marketplace currency code from marketplace code
 */
export const getMarketplaceCurrency = (marketplaceCode: string): CurrencyCode => {
  return MARKETPLACE_CURRENCY_MAP[marketplaceCode] || 'USD';
};

// Legacy export for backward compatibility
export const FIXED_RATES = FALLBACK_RATES;

/**
 * @deprecated Use fetchLiveRates instead
 */
export const updateExchangeRates = async (): Promise<ExchangeRates | null> => {
  const { rates } = await fetchLiveRates();
  return rates;
};
